// ColegiadoDetail.jsx
import React, { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Box,
  List,
  ListItem,
  ListItemText,
  Link,
  Divider,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import AvatarUsuario from "./AvatarUsuario";
import { Link as RouterLink } from "react-router-dom";
import Noticias from "../Colegiado/Noticias";

/* ====== helpers ====== */
const toISODate = (val) => {
  if (!val) return "";
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s.slice(0, 10);
};
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES") : "");
const joinDot = (arr) => (arr || []).filter(Boolean).join(" · ");
const listFromIds = (ids = [], mapa = {}, limit = 3) => {
  const nombres = (ids || []).map((id) => mapa[id]).filter(Boolean);
  if (nombres.length <= limit) return joinDot(nombres);
  return `${joinDot(nombres.slice(0, limit))} (+${nombres.length - limit})`;
};

/* Par etiqueta/valor compacto */
const Dato = ({ label, value }) => (
  <Box sx={{ minHeight: 68, display: "flex", flexDirection: "column", justifyContent: "center" }}>
    <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 700, wordBreak: "break-word" }}>{value || "—"}</Typography>
  </Box>
);

const ColegiadoDetail = ({ codigo }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [colegiado, setColegiado] = useState(null);
  const [loading, setLoading] = useState(true);

  // PERFIL
  const [perfil, setPerfil] = useState(null);

  // pendientes
  const [tramites, setTramites] = useState([]);
  const [subvenciones, setSubvenciones] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [pagos, setPagos] = useState([]);

  // catálogos
  const [mapaTramites, setMapaTramites] = useState({});
  const [mapaSubvenciones, setMapaSubvenciones] = useState({});
  const [mapaCursos, setMapaCursos] = useState({});
  const [mapaPaises, setMapaPaises] = useState({});
  const [mapaEstados, setMapaEstados] = useState({});
  const [mapaSituaciones, setMapaSituaciones] = useState({});
  const [mapaTitulaciones, setMapaTitulaciones] = useState({});
  const [mapaEspecialidades, setMapaEspecialidades] = useState({});
  const [mapaLugares, setMapaLugares] = useState({});

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  /* === Colegiado básico === */
  useEffect(() => {
    const fetchColegiado = async () => {
      try {
        if (!token) throw new Error("Token no disponible");
        const decoded = jwtDecode(token);
        const rol = decoded.rol || "COLEGIADO";
        const id = rol === "ADMIN" ? codigo : decoded.sub;
        const endpoint =
          rol === "ADMIN"
            ? `${API}/api/admin/colegiados/${id}/detalle`
            : `${API}/colegiado/detalle`;
        const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        setColegiado(res.data);
      } catch (err) {
        console.error("Error al obtener el colegiado:", err);
        setColegiado(null);
      } finally {
        setLoading(false);
      }
    };
    fetchColegiado();
  }, [codigo, API, token]);

  /* === Perfil completo === */
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/colegiado/perfil`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const c = res?.data || {};
        setPerfil({
          nombre: c.nombre ?? "",
          apellido_1: c.apellido_1 ?? c.apellido1 ?? "",
          apellido_2: c.apellido_2 ?? c.apellido2 ?? "",
          nif: c.nif ?? "",
          fecha_Nacimiento: toISODate(c.fecha_Nacimiento ?? c.fecha_nacimiento ?? ""),
          direccion: c.direccion ?? "",
          localidad: c.localidad ?? "",
          c_p_: c.c_p_ ?? c.cp ?? "",
          provincia: c.provincia ?? "",
          pais: /^\d+$/.test(String(c.pais ?? "")) ? Number(c.pais) : c.pais ?? "",
          nacionalidad: /^\d+$/.test(String(c.nacionalidad ?? "")) ? Number(c.nacionalidad) : c.nacionalidad ?? "",
          paisNacimientoId: c.paisNacimientoId ?? c.pais_nacimiento_id ?? "",
          telefono_1: c.telefono_1 ?? "",
          email: c.email ?? "",
          lugaresTrabajoIds:
            (Array.isArray(c.lugares_trabajo)
              ? c.lugares_trabajo.map((x) => x.id ?? x.codigo)
              : c.lugaresTrabajoIds ?? c.lugares_trabajo_ids) || [],
          estadoId: c.estadoId ?? c.estado_id ?? c?.estado?.id ?? "",
          titulacionPrincipalId:
            c.titulacionPrincipalId ?? c.titulacion_principal_id ?? c?.titulacion_principal?.id ?? "",
          situacionProfesionalId:
            c.situacionProfesionalId ?? c.situacion_profesional_id ?? c?.situacion_profesional?.id ?? "",
          especialidadesIds:
            (Array.isArray(c.especialidades)
              ? c.especialidades.map((x) => x.id ?? x.codigo)
              : c.especialidadesIds ?? c.especialidades_ids) || [],
          fecha1aColegiacion: toISODate(
            c.fecha_1a_colegiacion ??
            c.fecha1aColegiacion ??
            c.fechaPrimeraColegiacion ??
            c.fecha_primera_colegiacion ??
            ""
          ),
        });
      } catch (e) {
        console.error("Error cargando perfil:", e);
      }
    })();
  }, [API, token]);

  /* === Catálogos === */
  useEffect(() => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    if (!API) return;
    const toMap = (arr) =>
      (Array.isArray(arr) ? arr : []).reduce((acc, it) => {
        const id = it.id ?? it.codigo ?? it.value ?? it.key;
        const nombre = it.nombre ?? it.descripcion ?? it.titulo ?? String(id ?? "");
        if (id != null) acc[id] = nombre;
        return acc;
      }, {});
    Promise.allSettled([
      axios.get(`${API}/api/tramites`, auth),
      axios.get(`${API}/api/subvenciones`, auth),
      axios.get(`${API}/api/cursos/disponibles`),
      axios.get(`${API}/api/paises`),
      axios.get(`${API}/api/estados`),
      axios.get(`${API}/api/situaciones-profesionales`),
      axios.get(`${API}/api/titulaciones-principales`),
      axios.get(`${API}/api/especialidades`),
      axios.get(`${API}/api/lugares-trabajo`),
    ])
      .then(([tram, subv, cursos, pais, est, sit, tit, esp, lug]) => {
        if (tram.status === "fulfilled") setMapaTramites(toMap(tram.value.data));
        if (subv.status === "fulfilled") setMapaSubvenciones(toMap(subv.value.data));
        if (cursos.status === "fulfilled") setMapaCursos(toMap(cursos.value.data));
        if (pais.status === "fulfilled") setMapaPaises(toMap(pais.value.data));
        if (est.status === "fulfilled") setMapaEstados(toMap(est.value.data));
        if (sit.status === "fulfilled") setMapaSituaciones(toMap(sit.value.data));
        if (tit.status === "fulfilled") setMapaTitulaciones(toMap(tit.value.data));
        if (esp.status === "fulfilled") setMapaEspecialidades(toMap(esp.value.data));
        if (lug.status === "fulfilled") setMapaLugares(toMap(lug.value.data));
      })
      .catch((e) => console.error("Error catálogos", e));
  }, [API, token]);

  /* === Pendientes === */
  useEffect(() => {
    if (!colegiado?.codigo) return;
    axios
      .get(`${API}/api/colegiados/${colegiado.codigo}/tramites/page`, {
        params: { page: 0, size: 1000 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        setTramites(content.filter((t) => t.estado === "PENDIENTE"));
      });
  }, [colegiado?.codigo, API, token]);

  useEffect(() => {
    if (!colegiado?.codigo) return;
    axios
      .get(`${API}/api/subvenciones/misSubvenciones`, {
        params: { colegiadoId: colegiado.codigo },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSubvenciones((res.data || []).filter((s) => s.estado === "PENDIENTE")));
  }, [colegiado?.codigo, API, token]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/inscripciones/mias`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setInscripciones((res.data || []).filter((i) => i.estado === "PENDIENTE")));
  }, [API, token]);

  useEffect(() => {
    if (!colegiado?.codigo) return;
    axios
      .get(`${API}/api/pagos/colegiado/${colegiado.codigo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const m = (res.data || []).map((p) => ({
          id: p.id,
          concepto: p.concepto || "-",
          importe: p.importe != null ? p.importe.toFixed(2) : "-",
          estado: (p.estado || "").toUpperCase(),
        }));
        setPagos(m.filter((p) => p.estado === "PENDIENTE"));
      });
  }, [colegiado?.codigo, API, token]);

  if (loading) return <CircularProgress />;
  if (!colegiado) return <Typography color="error">Colegiado no encontrado</Typography>;

  return (
    <Grid container direction="column" spacing={2} sx={{ width: "100%", m: 0 }}>
      {/* FILA SUPERIOR: 2 columnas */}
      <Grid item>
        <Grid
          container
          spacing={2}
          alignItems={isDesktop ? "stretch" : "flex-start"}
          sx={{ flexWrap: isDesktop ? "nowrap" : "wrap" }}
        >
          {/* IZQUIERDA */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Paper
              elevation={10}
              sx={{
                p: 0,
                borderRadius: 4,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                background: "linear-gradient(135deg, #ffffff 0%, #f6f9ff 55%, #eef3ff 100%)",
                height: isDesktop ? "100%" : "auto"
              }}
            >
              {/* Banda superior */}
              <Box
                sx={{
                  px: 3, py: 1.5,
                  background: "linear-gradient(90deg, #1f3a8a 0%, #2563eb 50%, #0ea5e9 100%)",
                  color: "white",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 800, letterSpacing: 0.4 }}>TARJETA DE IDENTIFICACIÓN</Typography>
                <Box sx={{
                  width: 36, height: 26, borderRadius: 1,
                  background:
                    "repeating-linear-gradient(45deg, rgba(255,255,255,.85) 0, rgba(255,255,255,.85) 3px, rgba(255,255,255,.35) 3px, rgba(255,255,255,.35) 6px)",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,.6)",
                }} />
              </Box>

              {/* Encabezado con avatar + datos */}
              <Box sx={{ p: 3, pb: 1.5, display: "flex", gap: 3, alignItems: "center" }}>
                <AvatarUsuario usuario={colegiado} size={96} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1, textTransform: "uppercase" }}>
                    {(perfil?.nombre || colegiado?.nombre)} {perfil?.apellido_1 || colegiado?.apellido_1} {perfil?.apellido_2 || colegiado?.apellido_2}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6} sm={3}><Dato label="Nº COLEGIADO" value={colegiado?.codigo} /></Grid>
                    <Grid item xs={6} sm={3}><Dato label="NIF" value={perfil?.nif} /></Grid>
                    <Grid item xs={6} sm={3}><Dato label="ESTADO" value={mapaEstados[perfil?.estadoId] || colegiado?.estado} /></Grid>
                    <Grid item xs={6} sm={3}><Dato label="NACIMIENTO" value={formatDate(perfil?.fecha_Nacimiento)} /></Grid>
                  </Grid>
                </Box>
              </Box>

              {/* Contenido inferior */}
              <Box
                sx={{
                  px: 3, pt: 1, pb: 1,
                  flexGrow: 1,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                  gridAutoRows: "minmax(78px, auto)",
                  columnGap: 3,
                  rowGap: 1.5,
                }}
              >
                <Box>
                  <Dato label="TITULACIÓN PRINCIPAL" value={mapaTitulaciones[perfil?.titulacionPrincipalId]} />
                </Box>
                <Box sx={{ gridColumn: { xs: "1", sm: "2 / span 1", lg: "2 / span 2" } }}>
                  <Dato label="ESPECIALIDADES" value={listFromIds(perfil?.especialidadesIds, mapaEspecialidades)} />
                </Box>
                <Box>
                  <Dato label="SITUACIÓN PROFESIONAL" value={mapaSituaciones[perfil?.situacionProfesionalId]} />
                </Box>
                <Box sx={{ gridColumn: { xs: "1", sm: "2 / span 1", lg: "2 / span 2" } }}>
                  <Dato label="LUGAR(ES) DE TRABAJO" value={listFromIds(perfil?.lugaresTrabajoIds, mapaLugares, 3)} />
                </Box>
                <Box>
                  <Dato
                    label="NACIONALIDAD / PAÍS NAC."
                    value={joinDot([
                      (/^\d+$/.test(String(perfil?.nacionalidad)) ? mapaPaises[perfil?.nacionalidad] : perfil?.nacionalidad) || "",
                      mapaPaises[perfil?.paisNacimientoId] || ""
                    ])}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "1", sm: "2 / span 1", lg: "2 / span 1" } }}>
                  <Dato label="DIRECCIÓN" value={joinDot([perfil?.localidad, perfil?.provincia, perfil?.c_p_])} />
                </Box>
                <Box>
                  <Dato label="FECHA ALTA COLEGIACIÓN" value={formatDate(perfil?.fecha1aColegiacion)} />
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Pie */}
              <Box sx={{ px: 3, pb: 2 }}>
                <Box
                  sx={{
                    height: 30,
                    width: "100%",
                    background: "repeating-linear-gradient(90deg, #0f172a 0, #0f172a 2px, transparent 2px, transparent 4px)",
                    borderRadius: 1,
                  }}
                />
                <Typography sx={{ mt: 0.5, fontSize: 12, letterSpacing: 2, color: "text.secondary", textAlign: "right" }}>
                  {String(colegiado?.codigo || "").padStart(6, "0")} • {perfil?.nif || ""}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* DERECHA */}
          <Grid item xs={12} md={6} sx={{ display: "flex" }}>
            <Paper
              elevation={6}
              sx={{
                p: 2,
                borderRadius: 4,
                background: "#f9fbff",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: isDesktop ? "100%" : "auto"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
                <Typography variant="h6" color="primary" sx={{ fontSize: 20, fontWeight: 700 }}>
                  Tareas Pendientes
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 14, color: "text.secondary" }}>
                  (Desde aquí puedes acceder a la gestión de cada apartado haciendo click en "Ver" en cada sección.)
                </Typography>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                {/* Trámites */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: 16, fontWeight: 600 }}>Trámites</Typography>
                    <Link component={RouterLink} to="/tramites">Ver</Link>
                  </Box>
                  <List sx={{ "& .MuiListItemText-primary": { fontSize: 15.5, fontWeight: 500 }, mb: 1 }}>
                    {tramites.length > 0 ? (
                      tramites.map((t) => {
                        const tramiteId = t.tramiteId ?? t.tramite_id ?? t?.id?.tramiteId ?? t?.id?.tramite_id;
                        return (
                          <ListItem key={tramiteId} disableGutters>
                            <ListItemText primary={mapaTramites[tramiteId] || "Trámite pendiente"} />
                          </ListItem>
                        );
                      })
                    ) : (
                      <ListItem disableGutters><ListItemText primary="No hay trámites pendientes" /></ListItem>
                    )}
                  </List>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Subvenciones */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: 16, fontWeight: 600 }}>Subvenciones</Typography>
                    <Link component={RouterLink} to="/subvenciones">Ver</Link>
                  </Box>
                  <List sx={{ "& .MuiListItemText-primary": { fontSize: 15.5, fontWeight: 500 }, mb: 1 }}>
                    {subvenciones.length > 0 ? (
                      subvenciones.map((s) => (
                        <ListItem key={s.subvencionId} disableGutters>
                          <ListItemText primary={mapaSubvenciones[s.subvencionId] || "Subvención pendiente"} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disableGutters><ListItemText primary="No hay subvenciones pendientes" /></ListItem>
                    )}
                  </List>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Inscripciones */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: 16, fontWeight: 600 }}>Inscripciones</Typography>
                    <Link component={RouterLink} to="/cursos">Ver</Link>
                  </Box>
                  <List sx={{ "& .MuiListItemText-primary": { fontSize: 15.5, fontWeight: 500 }, mb: 1 }}>
                    {inscripciones.length > 0 ? (
                      inscripciones.map((i) => (
                        <ListItem key={i.id} disableGutters>
                          <ListItemText primary={mapaCursos[i?.curso?.id] || i?.curso?.nombre || "Curso pendiente"} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disableGutters><ListItemText primary="No hay inscripciones pendientes" /></ListItem>
                    )}
                  </List>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Pagos */}
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontSize: 16, fontWeight: 600 }}>Pagos</Typography>
                    <Link component={RouterLink} to="/pagos">Ver</Link>
                  </Box>
                  <List
                    sx={{
                      "& .MuiListItemText-primary": { fontSize: 15.5, fontWeight: 600 },
                      "& .MuiListItemText-secondary": { fontSize: 14.5 },
                    }}
                  >
                    {pagos.length > 0 ? (
                      pagos.map((p) => (
                        <ListItem key={p.id} disableGutters>
                          <ListItemText primary={p.concepto} secondary={`Importe: ${p.importe} €`} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disableGutters><ListItemText primary="No hay pagos pendientes" /></ListItem>
                    )}
                  </List>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* FILA INFERIOR: Noticias a ancho completo */}
      <Grid item>
        <Box sx={{ width: "100%" }}>
          <Noticias defaultPageSize={10} />
        </Box>
      </Grid>
    </Grid>
  );
};

export default ColegiadoDetail;
